import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ETL_RUNS_PAGE_SIZE, type ETLRunRow } from '../api/client';
import { FormPicker } from './FormPicker';
import { Badge, Button, Card, EmptyState, Field, SegmentedControl, inputStyle, tokens } from './ui/controls';

type RunSort = 'start_time' | 'created_at';
type BacklogSort = 'start_time' | 'run_name';
type BacklogStatusFilter = 'all' | 'pending' | 'in_progress' | 'completed';

const SUMMARY_BOXES = [
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
] as const;

function summaryKey(status: string) {
  return status.split('.').pop()?.toLowerCase() ?? '';
}

function backlogStatus(run: ETLRunRow) {
  return String(run.status ?? '').split('.').pop()?.toUpperCase() ?? '';
}

const BACKLOG_PAGE_SIZE = 8;

function formatStamp(value: string | null | undefined) {
  if (!value) return '';
  return String(value).replace('T', ' ').replace(/\.\d+.*$/, '');
}

/** `datetime-local` yields `YYYY-MM-DDTHH:mm`; Nominal wants whole seconds. */
function toIso(localValue: string) {
  return localValue ? `${localValue}:00` : '';
}

function statusTone(status: string) {
  const value = status.toLowerCase();
  if (value.includes('fail') || value.includes('error')) return 'danger';
  if (value.includes('complete') || value.includes('success') || value.includes('done')) return 'success';
  if (value.includes('progress') || value.includes('running')) return 'info';
  if (value.includes('pending')) return 'warning';
  return 'neutral';
}

export default function EtlDashboard() {
  const [summary, setSummary] = useState<Array<{ status: string; n: number }>>([]);
  const [runs, setRuns] = useState<ETLRunRow[]>([]);
  const [spec, setSpec] = useState<Record<string, unknown>>({});
  const [status, setStatus] = useState('');
  const [form, setForm] = useState({ run_name: '', asset: '', label: 'Harvest', start_time: '', end_time: '' });
  const [nera, setNera] = useState({ start_date: '', end_date: '', machine: '' });
  const [neraJobs, setNeraJobs] = useState<Array<{
    job_id: string;
    start_date: string;
    end_date: string;
    machine?: string | null;
    status: string;
    error_message?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  }>>([]);
  const [neraSubmitting, setNeraSubmitting] = useState(false);
  const [assets, setAssets] = useState<Array<{ rid: string; name: string }>>([]);
  const [assetError, setAssetError] = useState('');
  const assetSearchTimer = useRef<number | undefined>(undefined);
  const [sortBy, setSortBy] = useState<RunSort>('created_at');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [backlog, setBacklog] = useState<{ enabled: boolean; runs: ETLRunRow[] }>({ enabled: false, runs: [] });
  const [backlogQuery, setBacklogQuery] = useState('');
  const [backlogSort, setBacklogSort] = useState<BacklogSort>('start_time');
  const [backlogDescending, setBacklogDescending] = useState(true);
  const [backlogPaginate, setBacklogPaginate] = useState(false);
  const [backlogPage, setBacklogPage] = useState(0);
  const [backlogStatusFilter, setBacklogStatusFilter] = useState<BacklogStatusFilter>('all');
  const [selectedRunIds, setSelectedRunIds] = useState<number[]>([]);
  const [busyRunId, setBusyRunId] = useState<number | null>(null);
  const [fetchingNominal, setFetchingNominal] = useState(false);

  const refreshNeraJobs = useCallback(async () => {
    const res = await api.dataviewer.listNeraJobs();
    setNeraJobs(res.data.jobs || []);
  }, []);

  const refresh = useCallback(async () => {
    const [s, r, backlogRes] = await Promise.all([
      api.dataviewer.getETLSummary(),
      api.dataviewer.getETLRuns({ limit: ETL_RUNS_PAGE_SIZE, offset: page * ETL_RUNS_PAGE_SIZE, sort_by: sortBy }),
      api.dataviewer.getManualBacklog(),
    ]);
    setSummary(s.data.by_status || []);
    setRuns(r.data.runs);
    setTotal(r.data.total);
    setBacklog(backlogRes.data);
    await refreshNeraJobs();
  }, [page, sortBy, refreshNeraJobs]);

  useEffect(() => { refresh().catch((e) => setStatus(String(e))); }, [refresh]);

  const neraInFlight = neraJobs.some((job) => {
    const value = job.status.toLowerCase();
    return value.includes('pending') || value.includes('running') || value.includes('progress');
  });

  useEffect(() => {
    if (!backlog.enabled) return undefined;
    const id = window.setInterval(() => {
      api.dataviewer.getManualBacklog()
        .then((res) => setBacklog(res.data))
        .catch((e) => setStatus(String(e)));
    }, 15000);
    return () => window.clearInterval(id);
  }, [backlog.enabled]);

  useEffect(() => {
    if (!neraInFlight) return undefined;
    const id = window.setInterval(() => {
      refreshNeraJobs().catch((e) => setStatus(String(e)));
    }, 5000);
    return () => window.clearInterval(id);
  }, [neraInFlight, refreshNeraJobs]);

  const searchAssets = useCallback((text: string) => {
    window.clearTimeout(assetSearchTimer.current);
    assetSearchTimer.current = window.setTimeout(() => {
      api.dataviewer
        .getNominalAssets(text || undefined)
        .then((res) => { setAssets(res.data.assets || []); setAssetError(''); })
        .catch((e) => setAssetError(e.response?.data?.detail || e.message));
    }, 250);
  }, []);

  useEffect(() => {
    searchAssets('');
    return () => window.clearTimeout(assetSearchTimer.current);
  }, [searchAssets]);

  const rangeInvalid = useMemo(
    () => Boolean(form.start_time && form.end_time && form.end_time < form.start_time),
    [form.start_time, form.end_time],
  );

  const neraRangeInvalid = useMemo(
    () => Boolean(nera.start_date && nera.end_date && nera.end_date < nera.start_date),
    [nera.start_date, nera.end_date],
  );

  const filteredBacklog = useMemo(() => {
    const query = backlogQuery.trim().toLowerCase();
    const matched = backlog.runs.filter((run) => {
      if (backlogStatusFilter !== 'all' && backlogStatus(run).toLowerCase() !== backlogStatusFilter) {
        return false;
      }
      if (!query) return true;
      return [run.run_name, run.robot_name, run.start_time, run.end_time]
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
    return [...matched].sort((a, b) => {
      const left = String(a[backlogSort] ?? '');
      const right = String(b[backlogSort] ?? '');
      const compared = left < right ? -1 : left > right ? 1 : 0;
      return backlogDescending ? -compared : compared;
    });
  }, [backlog.runs, backlogQuery, backlogSort, backlogDescending, backlogStatusFilter]);

  const selectedInProgress = selectedRunIds.filter((id) =>
    backlog.runs.some((run) => run.id === id && backlogStatus(run) === 'IN_PROGRESS'),
  );
  const selectedCompleted = selectedRunIds.filter((id) =>
    backlog.runs.some((run) => run.id === id && backlogStatus(run) === 'COMPLETED'),
  );
  const selectedPending = selectedRunIds.filter((id) =>
    backlog.runs.some((run) => run.id === id && backlogStatus(run) === 'PENDING'),
  );
  const selectionAnchor = useRef<number | null>(null);
  const toggleSelected = (id: number) => {
    setSelectedRunIds((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const visibleBacklog = backlogPaginate
    ? filteredBacklog.slice(backlogPage * BACKLOG_PAGE_SIZE, (backlogPage + 1) * BACKLOG_PAGE_SIZE)
    : filteredBacklog;

  const selectBacklogRun = (run: ETLRunRow, index: number, event: React.MouseEvent) => {
    if (run.id == null || busyRunId != null) return;
    const id = Number(run.id);
    if (event.shiftKey) {
      const anchorIndex = selectionAnchor.current == null
        ? index
        : visibleBacklog.findIndex((item) => Number(item.id) === selectionAnchor.current);
      const start = Math.min(anchorIndex === -1 ? index : anchorIndex, index);
      const end = Math.max(anchorIndex === -1 ? index : anchorIndex, index);
      setSelectedRunIds(
        visibleBacklog
          .slice(start, end + 1)
          .map((item) => Number(item.id))
          .filter((item) => Number.isFinite(item)),
      );
      if (selectionAnchor.current == null) selectionAnchor.current = id;
      return;
    }
    selectionAnchor.current = id;
    if (event.ctrlKey || event.metaKey) {
      toggleSelected(id);
      return;
    }
    setSelectedRunIds((current) => (current.length === 1 && current[0] === id ? [] : [id]));
  };

  const backlogDirectionLabel = backlogSort === 'run_name'
    ? (backlogDescending ? 'Z → A' : 'A → Z')
    : (backlogDescending ? 'Newest first' : 'Oldest first');

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredBacklog.length / BACKLOG_PAGE_SIZE) - 1);
    if (backlogPage > lastPage) setBacklogPage(lastPage);
  }, [filteredBacklog.length, backlogPage]);

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>ETL dashboard</h2>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {SUMMARY_BOXES.map((box) => {
          const count = summary
            .filter((row) => summaryKey(String(row.status)) === box.key)
            .reduce((total, row) => total + Number(row.n), 0);
          return (
            <div
              key={box.key}
              style={{
                border: `1px solid ${tokens.border}`,
                borderRadius: tokens.radius,
                padding: '0.6rem 1rem',
                background: tokens.surface,
                minWidth: 110,
              }}
            >
              <Badge tone={statusTone(box.key)}>{box.label}</Badge>
              <div style={{ fontSize: '1.5rem', fontWeight: 600, marginTop: '0.35rem' }}>{count}</div>
            </div>
          );
        })}
      </div>

      {backlog.enabled && (
        <Card
          title="Nominal backlog"
          actions={
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <SegmentedControl<BacklogSort>
                options={[
                  { id: 'start_time', label: 'Run date' },
                  { id: 'run_name', label: 'Name' },
                ]}
                value={backlogSort}
                onChange={(id) => { setBacklogSort(id); setBacklogPage(0); }}
              />
              <Button
                onClick={() => { setBacklogDescending((current) => !current); setBacklogPage(0); }}
              >
                {backlogDirectionLabel}
              </Button>
              <SegmentedControl<BacklogStatusFilter>
                options={[
                  { id: 'all', label: 'All' },
                  { id: 'pending', label: 'Pending' },
                  { id: 'in_progress', label: 'In progress' },
                  { id: 'completed', label: 'Completed' },
                ]}
                value={backlogStatusFilter}
                onChange={(id) => { setBacklogStatusFilter(id); setBacklogPage(0); }}
              />
              <Button
                disabled={fetchingNominal || busyRunId != null}
                onClick={async () => {
                  setFetchingNominal(true);
                  try {
                    const res = await api.dataviewer.refreshNominalBacklog();
                    setStatus(`Fetched ${res.data.fetched} Nominal runs, added ${res.data.added}`);
                    await refresh();
                  } catch (e: any) {
                    setStatus(e.response?.data?.detail || e.message);
                  } finally {
                    setFetchingNominal(false);
                  }
                }}
              >
                {fetchingNominal ? 'Fetching…' : 'Fetch from Nominal'}
              </Button>
              <Button
                variant="primary"
                disabled={selectedPending.length === 0 || busyRunId != null}
                onClick={async () => {
                  setBusyRunId(-1);
                  const queued: number[] = [];
                  try {
                    for (const id of selectedPending) {
                      await api.dataviewer.ingestBacklogRun(id);
                      queued.push(id);
                    }
                    setSelectedRunIds((current) => current.filter((id) => !queued.includes(id)));
                    setStatus(`Ingesting ${queued.length} run${queued.length === 1 ? '' : 's'}`);
                    await refresh();
                  } catch (e: any) {
                    setSelectedRunIds((current) => current.filter((id) => !queued.includes(id)));
                    setStatus(e.response?.data?.detail || e.message);
                    await refresh();
                  } finally {
                    setBusyRunId(null);
                  }
                }}
              >
                Ingest
              </Button>
              <Button
                disabled={selectedInProgress.length + selectedCompleted.length === 0 || busyRunId != null}
                onClick={async () => {
                  if (
                    selectedCompleted.length > 0
                    && !window.confirm(
                      `Move ${selectedCompleted.length} completed run${selectedCompleted.length === 1 ? '' : 's'} back to pending and delete their ingested data?`,
                    )
                  ) {
                    return;
                  }
                  setBusyRunId(-1);
                  try {
                    if (selectedInProgress.length > 0) {
                      await api.dataviewer.requeueBacklogRuns(selectedInProgress);
                    }
                    for (const id of selectedCompleted) {
                      await api.dataviewer.resetCompletedRun(id);
                    }
                    const moved = selectedInProgress.length + selectedCompleted.length;
                    setSelectedRunIds([]);
                    setStatus(`Moved ${moved} to pending`);
                    await refresh();
                  } catch (e: any) {
                    setStatus(e.response?.data?.detail || e.message);
                  } finally {
                    setBusyRunId(null);
                  }
                }}
              >
                Move to pending
              </Button>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.82rem', color: tokens.text }}>
                <input
                  type="checkbox"
                  checked={backlogPaginate}
                  onChange={(e) => { setBacklogPaginate(e.target.checked); setBacklogPage(0); }}
                />
                Paginate
              </label>
            </div>
          }
        >
          <Field label="Search">
            <input
              style={inputStyle}
              value={backlogQuery}
              placeholder="Name, robot, or date"
              onChange={(e) => { setBacklogQuery(e.target.value); setBacklogPage(0); }}
            />
          </Field>
          {backlog.runs.length === 0 ? (
            <EmptyState>No pending, in-progress, or completed runs.</EmptyState>
          ) : filteredBacklog.length === 0 ? (
            <EmptyState>No matching runs.</EmptyState>
          ) : (
            <div style={{ maxHeight: 280, overflow: 'auto', display: 'grid', gap: '0.4rem', marginTop: '0.75rem' }}>
              {visibleBacklog.map((run, index) => {
                const selectable = run.id != null && ['PENDING', 'IN_PROGRESS', 'COMPLETED'].includes(backlogStatus(run));
                const selected = selectable && selectedRunIds.includes(Number(run.id));
                return (
                <div
                  key={run.id ?? run.run_name}
                  onClick={selectable && busyRunId == null ? (event) => selectBacklogRun(run, index, event) : undefined}
                  style={{
                    border: `1px solid ${selected ? tokens.primary : tokens.border}`,
                    borderRadius: tokens.radius,
                    padding: '0.6rem 0.8rem',
                    background: selected ? tokens.primarySoft : undefined,
                    cursor: selectable && busyRunId == null ? 'pointer' : undefined,
                    userSelect: selectable ? 'none' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    {selectable && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${run.run_name}`}
                        checked={selected}
                        readOnly
                        tabIndex={-1}
                        style={{ pointerEvents: 'none' }}
                      />
                    )}
                    <Badge tone={statusTone(String(run.status ?? ''))}>{String(run.status ?? 'unknown')}</Badge>
                    <strong style={{ fontSize: '0.92rem' }}>{String(run.run_name)}</strong>
                    <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>{String(run.robot_name ?? '')}</span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: tokens.textMuted, marginTop: '0.3rem' }}>
                    {formatStamp(run.start_time)}
                    {run.end_time ? ` → ${formatStamp(run.end_time)}` : ''}
                  </div>
                  {run.id != null && backlogStatus(run) === 'PENDING' && (
                    <div
                      style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Button
                        variant="primary"
                        disabled={busyRunId != null}
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                        onClick={async () => {
                          setBusyRunId(Number(run.id));
                          try {
                            await api.dataviewer.ingestBacklogRun(Number(run.id));
                            setStatus(`Ingesting ${run.run_name}`);
                            await refresh();
                          } catch (e: any) {
                            setStatus(e.response?.data?.detail || e.message);
                          } finally {
                            setBusyRunId(null);
                          }
                        }}
                      >
                        Ingest
                      </Button>
                      <Button
                        disabled={busyRunId != null}
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                        onClick={async () => {
                          setBusyRunId(Number(run.id));
                          try {
                            await api.dataviewer.deleteBacklogRun(Number(run.id));
                            setStatus(`Removed ${run.run_name}`);
                            await refresh();
                          } catch (e: any) {
                            setStatus(e.response?.data?.detail || e.message);
                          } finally {
                            setBusyRunId(null);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.6rem',
              marginTop: '0.75rem',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>
              {filteredBacklog.length === 0
                ? 'No runs'
                : backlogPaginate
                  ? `${backlogPage * BACKLOG_PAGE_SIZE + 1}–${backlogPage * BACKLOG_PAGE_SIZE + visibleBacklog.length} of ${filteredBacklog.length}`
                  : `${filteredBacklog.length} of ${backlog.runs.length}`}
            </span>
            {backlogPaginate && (
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <Button disabled={backlogPage === 0} onClick={() => setBacklogPage(backlogPage - 1)}>
                  Previous
                </Button>
                <Button
                  disabled={(backlogPage + 1) * BACKLOG_PAGE_SIZE >= filteredBacklog.length}
                  onClick={() => setBacklogPage(backlogPage + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card title="Create Nominal run">
        <div style={{ display: 'grid', gap: '0.75rem', maxWidth: 560 }}>
          <FormPicker kind="etl_nominal" onSpec={setSpec} />

          <Field label="Run name">
            <input
              style={inputStyle}
              value={form.run_name}
              onChange={(e) => setForm({ ...form, run_name: e.target.value })}
              placeholder="PSDN-15 Harvest 8/14-8/17"
            />
          </Field>

          <Field label="Asset / robot">
            <input
              style={inputStyle}
              list="nominal-assets"
              value={form.asset}
              onChange={(e) => { setForm({ ...form, asset: e.target.value }); searchAssets(e.target.value); }}
              placeholder="Search Nominal assets…"
            />
            <datalist id="nominal-assets">
              {assets.map((a) => (
                <option key={a.rid} value={a.name} />
              ))}
            </datalist>
            <span style={{ fontSize: '0.78rem', color: assetError ? tokens.danger : tokens.textFaint }}>
              {assetError || `${assets.length} asset${assets.length === 1 ? '' : 's'} from Nominal`}
            </span>
          </Field>

          <Field label="Label">
            <input
              style={inputStyle}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Field label="Start">
              <input
                type="datetime-local"
                step={1}
                style={inputStyle}
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              />
            </Field>
            <Field label="End">
              <input
                type="datetime-local"
                step={1}
                min={form.start_time || undefined}
                style={{ ...inputStyle, borderColor: rangeInvalid ? tokens.danger : tokens.border }}
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              />
            </Field>
          </div>
          {rangeInvalid && (
            <span style={{ fontSize: '0.8rem', color: tokens.danger }}>End must be after start.</span>
          )}

          <div>
            <Button
              variant="primary"
              disabled={!form.run_name || !form.start_time || !form.end_time || rangeInvalid}
              onClick={async () => {
                try {
                  await api.dataviewer.createNominalRun({
                    ...spec,
                    ...form,
                    start_time: toIso(form.start_time),
                    end_time: toIso(form.end_time),
                  });
                  setStatus('Nominal run enqueued');
                  refresh();
                } catch (e: any) {
                  setStatus(e.response?.data?.detail || e.message);
                }
              }}
            >
              Create and enqueue
            </Button>
          </div>
        </div>
      </Card>

      <Card title="NERA backfill">
        <div style={{ display: 'grid', gap: '0.75rem', maxWidth: 560 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Field label="Start date">
              <input
                type="date"
                style={inputStyle}
                value={nera.start_date}
                onChange={(e) => setNera({ ...nera, start_date: e.target.value })}
              />
            </Field>
            <Field label="End date">
              <input
                type="date"
                min={nera.start_date || undefined}
                style={{ ...inputStyle, borderColor: neraRangeInvalid ? tokens.danger : tokens.border }}
                value={nera.end_date}
                onChange={(e) => setNera({ ...nera, end_date: e.target.value })}
              />
            </Field>
          </div>
          {neraRangeInvalid && (
            <span style={{ fontSize: '0.8rem', color: tokens.danger }}>End date must be on or after start date.</span>
          )}

          <Field label="Machine (optional)">
            <input
              style={inputStyle}
              list="nominal-assets"
              value={nera.machine}
              onChange={(e) => { setNera({ ...nera, machine: e.target.value }); searchAssets(e.target.value); }}
              placeholder="Search Nominal assets…"
            />
          </Field>

          <div>
            <Button
              variant="primary"
              disabled={!nera.start_date || !nera.end_date || neraRangeInvalid || neraSubmitting}
              onClick={async () => {
                const params: Record<string, string> = { start_date: nera.start_date, end_date: nera.end_date };
                if (nera.machine) params.machine = nera.machine;
                setNeraSubmitting(true);
                try {
                  await api.dataviewer.neraBackfill(params);
                  setStatus('Backfill submitted');
                  try {
                    await refreshNeraJobs();
                  } catch (e: any) {
                    setStatus(`Backfill submitted; status refresh failed: ${e.response?.data?.detail || e.message}`);
                  }
                } catch (e: any) {
                  setStatus(e.response?.data?.detail || e.message);
                } finally {
                  setNeraSubmitting(false);
                }
              }}
            >
              {neraSubmitting ? 'Submitting…' : 'Submit backfill'}
            </Button>
          </div>

          {neraJobs.length === 0 ? (
            <EmptyState>No NERA backfill jobs yet.</EmptyState>
          ) : (
            <div style={{ display: 'grid', gap: '0.4rem' }}>
              {neraJobs.map((job) => (
                <div
                  key={job.job_id}
                  style={{
                    border: `1px solid ${tokens.border}`,
                    borderRadius: tokens.radius,
                    padding: '0.6rem 0.8rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <Badge tone={statusTone(job.status)}>{job.status}</Badge>
                    <strong style={{ fontSize: '0.92rem' }}>
                      {job.start_date} → {job.end_date}
                    </strong>
                    {job.machine ? <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>{job.machine}</span> : null}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: tokens.textMuted, marginTop: '0.3rem' }}>
                    {job.updated_at ? String(job.updated_at).replace('T', ' ').replace(/\.\d+$/, '') : job.job_id}
                    {job.error_message ? ` · ${job.error_message}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {status && <p style={{ fontSize: '0.85rem', color: tokens.textMuted }}>{status}</p>}

      <Card
        title="Runs"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <SegmentedControl<RunSort>
              options={[
                { id: 'created_at', label: 'Ingest date' },
                { id: 'start_time', label: 'Run date' },
              ]}
              value={sortBy}
              onChange={(id) => { setSortBy(id); setPage(0); }}
            />
            <Button onClick={() => refresh()}>Refresh</Button>
          </div>
        }
      >
        {runs.length === 0 ? (
          <EmptyState>No ETL runs found.</EmptyState>
        ) : (
          <div style={{ maxHeight: 480, overflow: 'auto', display: 'grid', gap: '0.4rem' }}>
            {runs.map((run, idx) => (
              <div
                key={idx}
                style={{
                  border: `1px solid ${tokens.border}`,
                  borderRadius: tokens.radius,
                  padding: '0.6rem 0.8rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <Badge tone={statusTone(String(run.status ?? ''))}>{String(run.status ?? 'unknown')}</Badge>
                  <strong style={{ fontSize: '0.92rem' }}>{String(run.run_name)}</strong>
                  {run.failure_class ? <Badge tone="danger">{String(run.failure_class)}</Badge> : null}
                </div>
                <div style={{ fontSize: '0.82rem', color: tokens.textMuted, marginTop: '0.3rem' }}>
                  {String(run.robot_name ?? '')}
                  {run.task_memory ? ` · memory ${String(run.task_memory)}` : ''}
                  {run.error_message || run.stop_reason ? ` · ${String(run.error_message || run.stop_reason)}` : ''}
                </div>
                {run.id != null && (
                  <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    {backlog.enabled && backlogStatus(run) === 'COMPLETED' && (
                      <Button
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                        disabled={busyRunId != null}
                        onClick={async () => {
                          const id = Number(run.id);
                          if (!window.confirm(`Move ${run.run_name} back to pending and delete its ingested data?`)) return;
                          setBusyRunId(id);
                          try {
                            const res = await api.dataviewer.resetCompletedRun(id);
                            setStatus(`Moved ${run.run_name} to pending, deleted ${res.data.deleted_rows} rows`);
                            await refresh();
                          } catch (e: any) {
                            setStatus(e.response?.data?.detail || e.message);
                          } finally {
                            setBusyRunId(null);
                          }
                        }}
                      >
                        Move to pending
                      </Button>
                    )}
                    <Button
                      style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                      onClick={() => api.dataviewer.patchCleaning(Number(run.id), { cleaning_status: 'done' }).then(refresh)}
                    >
                      Mark cleaned
                    </Button>
                    <Button
                      style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                      onClick={() => api.dataviewer.patchCleaning(Number(run.id), { needs_attention: true }).then(refresh)}
                    >
                      Needs attention
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.6rem',
            marginTop: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>
            {total === 0
              ? 'No runs'
              : `${page * ETL_RUNS_PAGE_SIZE + 1}–${page * ETL_RUNS_PAGE_SIZE + runs.length} of ${total}`}
          </span>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <Button disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button
              disabled={(page + 1) * ETL_RUNS_PAGE_SIZE >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
